import type { Component } from 'solid-js';
import { requestLearningQueue } from './learning-primary-request';
import { requestOperationsAuditInitialPage } from './operations-audit-request';
import { requestPresentationLibrary } from './presentation-primary-request';
import {
  defaultSchedulePrimaryPageRequest,
  requestScheduleWorkspace,
} from './schedule-primary-request';
import {
  messagingWorkspaceQueryNamespace,
  type PrimaryWorkspaceRouteId,
  type WorkspaceRouteMetadata,
  workspaceRouteMetadataById,
  workspaceRouteMetadataForHref,
} from './workspace-route-metadata';

export type WorkspaceRouteLoadContext = {
  href: string;
  accountId: number;
  activeRole: string;
};

export type WorkspaceRouteDescriptor = WorkspaceRouteMetadata & {
  loadModule: () => Promise<unknown>;
  loadComponent: () => Promise<{ default: Component }>;
  loadData: (signal: AbortSignal, context: WorkspaceRouteLoadContext) => Promise<unknown>;
  queryKey: (context: WorkspaceRouteLoadContext) => string;
};

const dashboardModule = () => import('./DashboardPage');
const peopleModule = () => import('./PeoplePage');
const groupsModule = () => import('./GroupsPage');
const curriculumModule = () => import('./LevelPlanLibraryPage');
const scheduleModule = () => import('./SchedulePage');
const presentationsModule = () => import('./PresentationLibraryPage');
const deliveriesModule = () => import('./DeliveriesPage');
const learningModule = () => import('./TasksPage');
const assessmentModule = () => import('./AssessmentPage');
const messagesModule = () => import('./MessagesPage');
const privacyModule = () => import('./PrivacyPage');
const accessModule = () => import('./AdminAccessPage');
const operationsModule = () => import('./OperationsPage');
const governanceModule = () => import('./GovernancePage');

const descriptor = (
  id: PrimaryWorkspaceRouteId,
  implementation: Pick<
    WorkspaceRouteDescriptor,
    'loadModule' | 'loadComponent' | 'loadData' | 'queryKey'
  >,
): WorkspaceRouteDescriptor => ({ ...workspaceRouteMetadataById(id), ...implementation });

export const workspaceRouteDescriptors: readonly WorkspaceRouteDescriptor[] = [
  descriptor('overview', {
    loadModule: dashboardModule,
    loadComponent: async () => ({ default: (await dashboardModule()).DashboardPage }),
    loadData: async (signal) => (await dashboardModule()).loadDashboard(signal),
    queryKey: () => workspaceRouteMetadataById('overview').queryNamespace,
  }),
  descriptor('people', {
    loadModule: peopleModule,
    loadComponent: async () => ({ default: (await peopleModule()).PeoplePage }),
    loadData: async (signal) => (await peopleModule()).loadPeopleWorkspace(signal),
    queryKey: () => workspaceRouteMetadataById('people').queryNamespace,
  }),
  descriptor('groups', {
    loadModule: groupsModule,
    loadComponent: async () => ({ default: (await groupsModule()).GroupsPage }),
    loadData: async (signal) => (await groupsModule()).loadGroupsWorkspace(signal),
    queryKey: () => workspaceRouteMetadataById('groups').queryNamespace,
  }),
  descriptor('curriculum', {
    loadModule: curriculumModule,
    loadComponent: async () => ({ default: (await curriculumModule()).LevelPlanLibraryPage }),
    loadData: async (signal) => (await curriculumModule()).loadLevelPlanLibrary(signal),
    queryKey: () => workspaceRouteMetadataById('curriculum').queryNamespace,
  }),
  descriptor('schedule', {
    loadModule: scheduleModule,
    loadComponent: async () => ({ default: (await scheduleModule()).SchedulePage }),
    loadData: async (signal, context) => {
      const module = await scheduleModule();
      const request = module.schedulePageRequestFromHref(context.href);
      if (context.activeRole === 'STUDENT') {
        return module.loadStudentScheduleWorkspace(signal, request);
      }
      if (context.activeRole === 'GUARDIAN') {
        return module.loadGuardianScheduleWorkspace(signal, request);
      }
      const url = new URL(context.href, 'https://workspace.invalid');
      if (url.pathname === '/schedule' && url.search === '') {
        const defaultRequest = defaultSchedulePrimaryPageRequest();
        const data = await requestScheduleWorkspace(signal, defaultRequest);
        return module.parseScheduleWorkspace(data, defaultRequest);
      }
      return module.loadScheduleWorkspace(signal, request);
    },
    queryKey: () => workspaceRouteMetadataById('schedule').queryNamespace,
  }),
  descriptor('presentations', {
    loadModule: presentationsModule,
    loadComponent: async () => ({ default: (await presentationsModule()).PresentationLibraryPage }),
    loadData: async (signal) => {
      const request = requestPresentationLibrary(signal);
      const parser = import('./presentation-studio-data');
      const [data, module] = await Promise.all([request, parser]);
      return module.parsePresentationLibrary(data);
    },
    queryKey: () => workspaceRouteMetadataById('presentations').queryNamespace,
  }),
  descriptor('deliveries', {
    loadModule: deliveriesModule,
    loadComponent: async () => ({ default: (await deliveriesModule()).DeliveriesPage }),
    loadData: async (signal) => (await import('./delivery-data')).loadDeliveryWorkspace(signal),
    queryKey: () => workspaceRouteMetadataById('deliveries').queryNamespace,
  }),
  descriptor('learning', {
    loadModule: learningModule,
    loadComponent: async () => ({ default: (await learningModule()).TasksPage }),
    loadData: async (signal) => {
      const request = requestLearningQueue(signal);
      const parser = import('./assessment-data');
      const [data, module] = await Promise.all([request, parser]);
      return module.parseLearningQueue(data);
    },
    queryKey: () => workspaceRouteMetadataById('learning').queryNamespace,
  }),
  descriptor('assessment', {
    loadModule: assessmentModule,
    loadComponent: async () => ({ default: (await assessmentModule()).AssessmentPage }),
    loadData: async (signal) => (await import('./assessment-data')).loadAssessmentWorkspace(signal),
    queryKey: () => workspaceRouteMetadataById('assessment').queryNamespace,
  }),
  descriptor('messages', {
    loadModule: messagesModule,
    loadComponent: async () => ({ default: (await messagesModule()).MessagesPage }),
    loadData: async (signal, context) => {
      const url = new URL(context.href, 'https://workspace.invalid');
      const conversationId = Number(/^\/messages\/(\d+)$/.exec(url.pathname)?.[1]);
      const conversationCursor = positiveInteger(url.searchParams.get('conversationCursor'));
      const messageCursor = positiveInteger(url.searchParams.get('messageCursor'));
      return (await messagesModule()).loadMessagingWorkspace(
        signal,
        Number.isSafeInteger(conversationId) && conversationId > 0 ? conversationId : undefined,
        {
          ...(conversationCursor ? { conversationCursor } : {}),
          ...(messageCursor ? { messageCursor } : {}),
        },
      );
    },
    queryKey: (context) => messagesQueryKey(context.href),
  }),
  descriptor('privacy', {
    loadModule: privacyModule,
    loadComponent: async () => ({ default: (await privacyModule()).PrivacyPage }),
    loadData: async (signal) => (await privacyModule()).loadParticipantWorkspace(signal),
    queryKey: () => workspaceRouteMetadataById('privacy').queryNamespace,
  }),
  descriptor('access', {
    loadModule: accessModule,
    loadComponent: async () => ({ default: (await accessModule()).AdminAccessPage }),
    loadData: async (signal) => (await accessModule()).loadAccessWorkspace(signal),
    queryKey: () => workspaceRouteMetadataById('access').queryNamespace,
  }),
  descriptor('operations', {
    loadModule: operationsModule,
    loadComponent: async () => ({ default: (await operationsModule()).OperationsAuditPage }),
    loadData: async (signal) => {
      const request = requestOperationsAuditInitialPage(signal);
      const parser = import('./operations-data');
      const [data, module] = await Promise.all([request, parser]);
      return module.parseOperationsAuditInitialPage(data);
    },
    queryKey: () => workspaceRouteMetadataById('operations').queryNamespace,
  }),
  descriptor('governance', {
    loadModule: governanceModule,
    loadComponent: async () => ({ default: (await governanceModule()).AcademySettingsPage }),
    loadData: async (signal) => (await governanceModule()).loadGovernance(signal),
    queryKey: () => workspaceRouteMetadataById('governance').queryNamespace,
  }),
];

const positiveInteger = (value: string | null) => {
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const messagesQueryKey = (href: string) => {
  const url = new URL(href, 'https://workspace.invalid');
  const conversationId = positiveInteger(/^\/messages\/(\d+)$/.exec(url.pathname)?.[1] ?? null);
  const conversationCursor = positiveInteger(url.searchParams.get('conversationCursor'));
  const messageCursor = positiveInteger(url.searchParams.get('messageCursor'));
  return messagingWorkspaceQueryNamespace({
    ...(conversationId ? { conversationId } : {}),
    ...(conversationCursor ? { conversationCursor } : {}),
    ...(messageCursor ? { messageCursor } : {}),
  });
};

export const workspaceRouteDescriptorById = (id: PrimaryWorkspaceRouteId) => {
  const result = workspaceRouteDescriptors.find((candidate) => candidate.id === id);
  if (!result) throw new Error(`Missing workspace route descriptor: ${id}`);
  return result;
};

export const workspaceRouteDescriptorForHref = (href: string) => {
  const metadata = workspaceRouteMetadataForHref(href);
  return metadata ? workspaceRouteDescriptorById(metadata.id) : null;
};
